import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink flex items-center justify-center px-6">
      <div className="max-w-md w-full border border-white/10 bg-brand-surface rounded-sm p-10 text-center">
        {children}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setState("error"); return; }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (res.data.payment_status === "failed") { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state === "polling") {
    return (
      <Shell>
        <Loader2 size={40} className="text-brand-orange animate-spin mx-auto" />
        <h1 className="mt-6 font-heading font-bold uppercase text-2xl tracking-tight">Confirming payment…</h1>
        <p className="mt-3 text-brand-muted text-sm">Hang tight while we lock in your session.</p>
      </Shell>
    );
  }

  if (state === "paid") {
    return (
      <Shell>
        <CheckCircle2 size={44} className="text-[#34C759] mx-auto" />
        <h1 className="mt-6 font-heading font-black uppercase text-3xl tracking-tighter" data-testid="payment-success">You're booked.</h1>
        <p className="mt-3 text-brand-muted text-sm">Your session is confirmed and added to your portal. See you on the range.</p>
        <button onClick={() => navigate("/dashboard")} data-testid="success-dashboard-btn" className="group mt-8 w-full flex items-center justify-center gap-2 bg-brand-orange text-brand-bg font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-brand-ink transition-colors">
          Go to my portal <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <XCircle size={44} className="text-[#FF3B30] mx-auto" />
      <h1 className="mt-6 font-heading font-bold uppercase text-2xl tracking-tight">Payment not confirmed</h1>
      <p className="mt-3 text-brand-muted text-sm">We couldn't confirm your payment yet. Check your bookings or try again.</p>
      <button onClick={() => navigate("/dashboard")} data-testid="error-dashboard-btn" className="mt-8 w-full font-mono text-xs uppercase tracking-[0.2em] border border-white/20 px-6 py-4 hover:border-brand-orange transition-colors">
        Back to portal
      </button>
    </Shell>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <Shell>
      <XCircle size={44} className="text-brand-brass mx-auto" />
      <h1 className="mt-6 font-heading font-bold uppercase text-2xl tracking-tight" data-testid="payment-cancel">Checkout cancelled</h1>
      <p className="mt-3 text-brand-muted text-sm">No charge was made. Your slot is still held as pending in your portal.</p>
      <button onClick={() => navigate("/dashboard")} data-testid="cancel-dashboard-btn" className="mt-8 w-full font-mono text-xs uppercase tracking-[0.2em] border border-white/20 px-6 py-4 hover:border-brand-orange transition-colors">
        Back to portal
      </button>
    </Shell>
  );
}
